import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTheme } from '../../context/ThemeContext';
import { ChevronDown, ChevronUp } from 'lucide-react';
import CreditCards from '../../images/credit-cards.png'
import ShippingFlowFooter from '../../components/ShippingFlowFooter';
import { useFormStore } from '../../stores/formData';
import LoadingState from '../../components/LoadingState';
import PromoCodeInput from './PromoCodeInput';
import PaymentTabs from './PaymentTabs';
import useOrderApi from '../../hooks/useOrderApi';
import PreviousCardSelector from './PreviousCardSelector';
import ClubBillingSelector from './ClubBillingSelector';
import BillingAddressSelector from './BillingAddressSelector';  

const PaymentForm = ({ 
  onCardDataChange,
  onApplyPromoCode,
  onRemovePromoCode,
  createPaymentMethod,
  onClickContinue,
  onClickBack,
  applyingPromoCode,
  appliedPromoCode,
  promoCodeError,
  creatingOrder,
  cartError,
  orderError,
  orderProgress
}) => {
  // Local state for card fields (not saved to formData)
  const [cardData, setCardData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    number: '',
    expiry: '',
    cvv: '',
    billingCountry: 'US',
    billingZipCode: ''
  });

  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [expanded, setExpanded] = useState(true);

  const [validationErrors, setValidationErrors] = useState({});
  const [paymentTab, setPaymentTab] = useState('new-card');
  const [clubBillingOptions, setClubBillingOptions] = useState([]);
  const [loadingClubs, setLoadingClubs] = useState(false);
  const [loadedClubs, setLoadedClubs] = useState(false);
  
  const [previousCardOptions, setPreviousCardOptions] = useState([]);
  const [loadingPreviousCards, setLoadingPreviousCards] = useState(false);
  const [loadedPreviousCards, setLoadedPreviousCards] = useState(false);

  const formData = useFormStore((state) => state.formData);
  const actions = useFormStore((state) => state.actions);
  const paymentMethod = useFormStore((state) => state.formData.paymentMethod);
  const customerInfo = useFormStore((state) => state.formData.customerInfo);
  const showCouponField = true;
  
  const {
    getUserPaymentMethods,
    getClubBillingClubs
  } = useOrderApi();

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const handlePaymentTabChange = useCallback((tabId) => {
    setPaymentTab(tabId);
    if (tabId === 'previous-cards' 
       && previousCardOptions?.length === 0
       && !loadingPreviousCards
       && !loadedClubs) {
        setLoadingPreviousCards(true);
        const getPreviousCards = async () => {
        try {
          const cards = await getUserPaymentMethods(formData);
          const slicedCards = cards.slice(0, 5); // Limit to 5 cards
          setPreviousCardOptions(slicedCards);
          setLoadedPreviousCards(true);
        }
        catch (error) {
          console.error('Error fetching previous card data:', error);
        }
        finally {
          setLoadingPreviousCards(false);
        }
      };
      getPreviousCards();
    }
    if (tabId === 'club-billing' 
       && clubBillingOptions?.length === 0
       && !loadingClubs
       && !loadedClubs) {
        setLoadingClubs(true);
        const getClubs = async () => {
        try {
          const clubs = await getClubBillingClubs(formData.customerInfo?.authToken);
          setClubBillingOptions(clubs.club_billing || []);
          setLoadedClubs(true);
        }
        catch (error) {
          console.error('Error fetching club billing data:', error);
        }
        finally {
          setLoadingClubs(false);
        }
      };
      getClubs();
    }
  }, [loadedClubs, loadingClubs, clubBillingOptions, formData.customerInfo?.authToken, getClubBillingClubs]);

  const validateCardData = () => {
    const errors = {};
    
    if (!cardData.firstName.trim()) {
      errors.firstName = 'Cardholder first name is required';
    }

    if (!cardData.lastName.trim()) {
      errors.lastName = 'Cardholder last name is required';
    }

    // if (!cardData.email.trim()) {
    //   errors.email = 'Payer email is required';
    // } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cardData.email)) {
    //   errors.email = 'Please enter a valid email address';
    // }
    
    if (!cardData.number.trim()) {
      errors.number = 'Card number is required';
    } else if (cardData.number.replace(/\s/g, '').length < 13) {
      errors.number = 'Card number is too short';
    }
    
    if (!cardData.expiry.trim()) {
      errors.expiry = 'Expiry date is required';
    } else if (!/^\d{2}\/\d{2}$/.test(cardData.expiry)) {
      errors.expiry = 'Format: MM/YY';
    }
    
    if (!cardData.cvv.trim()) {
      errors.cvv = 'CVV is required';
    } else if (cardData.cvv.length < 3) {
      errors.cvv = 'CVV must be 3-4 digits';
    }

    if (!cardData.billingCountry.trim()) {
      errors.billingCountry = 'Country is required';
    }

    if (!cardData.billingZipCode.trim()) {
      errors.billingZipCode = 'ZIP/Postal code is required';
    } else if (cardData.billingCountry === 'US' && !/^\d{5}(-\d{4})?$/.test(cardData.billingZipCode)) {
      errors.billingZipCode = 'Format: 12345 or 12345-1234';
    }
    
    return errors;
  };

  const isFirstRender = useRef(true);
  useEffect(() => {
    // Skip first render to avoid initial empty state notification
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    
    // Notify parent of card data changes
    onCardDataChange?.(cardData);
  }, [cardData]);

  const handleCardholderFirstNameChange = useCallback((e) => {
    if (validationErrors.firstName) {
      setValidationErrors(prev => ({ ...prev, firstName: null }));
    }
    setCardData(prev => ({ ...prev, firstName: e.target.value }));
  }, [validationErrors]);

  const handleCardholderLastNameChange = useCallback((e) => {
    if (validationErrors.lastName) {
      setValidationErrors(prev => ({ ...prev, lastName: null }));
    }
    setCardData(prev => ({ ...prev, lastName: e.target.value }));
  }, [validationErrors]);

  const handleSelectClubBilling = useCallback((club) => {
    setCardData(prev => ({ ...prev, 
      paymentType: 'club-billing', 
      clubBilling: club,
      previousCard: null,
    }));
  }, [validationErrors]);

  const handleSelectPreviousCard = useCallback((card) => {
    setCardData(prev => ({ ...prev, 
      clubBilling: null,
      paymentType: 'previous-card', 
      previousCard: card, 
    }));
  }, [validationErrors]);

  const handleCardNumberChange = useCallback((e) => {
    const value = e.target.value.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
    if (value.replace(/\s/g, '').length <= 16) {
      setCardData(prev => ({ ...prev, number: value }));
    }
    if (validationErrors.number) {
      setValidationErrors(prev => ({ ...prev, number: null }));
    }
  }, [validationErrors]);

  const handleExpiryChange = useCallback((e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length >= 2) {
      value = value.substring(0, 2) + '/' + value.substring(2, 4);
    }
    setCardData(prev => ({ ...prev, expiry: value }));
    if (validationErrors.expiry) {
      setValidationErrors(prev => ({ ...prev, expiry: null }));
    }
  }, [validationErrors]);

  const handleApplyPromoCode = useCallback((value) => {
    setCardData(prev => ({ ...prev, couponCode: value }));
    if (validationErrors.couponCode) {
      setValidationErrors(prev => ({ ...prev, couponCode: null }));
    }
    onApplyPromoCode?.(value);
  }, [validationErrors]);

  const handleRemovePromoCode = useCallback(() => {
    setCardData(prev => ({ ...prev, couponCode: '' }));
    if (validationErrors.couponCode) {
      setValidationErrors(prev => ({ ...prev, couponCode: null }));
    }
    onRemovePromoCode?.();
  }, [validationErrors]);

  const handleCvvChange = useCallback((e) => {
    const value = e.target.value.replace(/\D/g, '').substring(0, 4);
    setCardData(prev => ({ ...prev, cvv: value }));
    if (validationErrors.cvv) {
      setValidationErrors(prev => ({ ...prev, cvv: null }));
    }
  }, [validationErrors]);

  const handleBillingCountryChange = useCallback((value) => {
    setCardData(prev => ({ ...prev, billingCountry: value }));
    if (validationErrors.billingCountry) {
      setValidationErrors(prev => ({ ...prev, billingCountry: null }));
    }
  }, [validationErrors]);

  const handleBillingZipCodeChange = useCallback((value) => {
    setCardData(prev => ({ ...prev, billingZipCode: value }));
    if (validationErrors.billingZipCode) {
      setValidationErrors(prev => ({ ...prev, billingZipCode: null }));
    }
  }, [validationErrors]);

  const handleEditPaymentMethod = () => {
    // Clear the payment method to expand the form
    actions.form.updateField('paymentMethod', null)
    setValidationErrors({});
  };

  const containerClassName = clsx(
      'rounded-lg border transition-all duration-200',
      {
        'bg-slate-800 border-slate-700': isDark,
        'bg-slate-200 border-gray-200': !isDark
      }
    );
  
  const headerClassName = clsx(
    'flex items-center justify-between p-4 cursor-pointer',
    {
      'hover:bg-slate-700': isDark,
      'hover:bg-gray-100': !isDark
    }
  );

  const titleClassName = clsx(
    'text-lg font-medium flex items-center gap-2',
    {
      'text-slate-200': isDark,
      'text-gray-900': !isDark
    }
  );

  const labelClassName = clsx(
    'text-sm mb-2 font-medium',
    {
      'text-slate-200': isDark,
      'text-gray-500': !isDark
    }
  );

  const inputClassName = clsx(
    'w-full text-sm px-3 py-3 border rounded-lg',
    {
      'text-slate-200': isDark,
      'text-gray-500': !isDark
    }
  );

  const iconClassName = clsx(
    'w-4 h-4',
    {
      'text-slate-400': isDark,
      'text-gray-500': !isDark
    }
  );

  // Check if payment method already exists
  const paymentMethodExists = paymentMethod?.id;
  const isFormValid = cardData.firstName && cardData.lastName &&
    cardData.email && cardData.number && cardData.expiry &&
    cardData.cvv && cardData.billingCountry && cardData.billingZipCode;

  // If payment method exists, show collapsed view
  if (paymentMethodExists) {
    return (
        <div className={containerClassName}>
          <div className={headerClassName} onClick={toggleExpanded}>
            <h3 className={titleClassName}>
              Payments
            </h3>
            {expanded ? (
              <ChevronUp className={iconClassName} />
            ) : (
              <ChevronDown className={iconClassName} />
            )}
          </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={clsx(
                    'w-8 h-8 bg-green-500 text-white rounded-full ',
                    'flex items-center justify-center text-sm font-semibold')}>
                    ✓
                  </div>
                  <div>
                    <p className="font-medium text-green-900">
                      Payment Method Saved
                    </p>
                    <p className="text-sm text-green-700">
                      {paymentMethod.cardholderName} • {paymentMethod.payerEmail}
                    </p>
                    <p className="text-sm text-green-700">
                      •••• {paymentMethod.lastFour} • 
                      Expires: {paymentMethod.expiryDate} • 
                      {paymentMethod.billingCountry} {paymentMethod.billingZipCode}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleEditPaymentMethod}
                  className="text-sm text-green-700 hover:text-green-900 underline"
                >
                  Change Card
                </button>
              </div>
            </div>
          </div>

      );
    }

    return (
      <>
      <div className={containerClassName}>
          <div className={headerClassName} onClick={toggleExpanded}>
            <h3 className={titleClassName}>
              Payments
            </h3>
            {expanded ? (
              <ChevronUp className={iconClassName} />
            ) : (
              <ChevronDown className={iconClassName} />
            )}
          </div>
        {expanded && (
          <div className="p-4 bg-white">
            {showCouponField && (
              <>
              <h3>Coupon code</h3>
              <div className="flex flex-row gap-3 my-6">
                <div className="flex-1">
                  <PromoCodeInput
                    onApply={handleApplyPromoCode}
                    onRemove={handleRemovePromoCode}
                    appliedCode={promoCodeError ? null : formData.couponCode}
                    appliedPromoCode={appliedPromoCode}
                    error={validationErrors.couponCode || promoCodeError}
                    isLoading={applyingPromoCode}
                    disabled={applyingPromoCode}
                  />
                </div>
              </div>
              </>
            )}
            <div className="my-3">
              <div className="flex flex-row justify-between mb-1">
                <h3>Card details</h3>
                <img src={CreditCards} alt="Credit Cards" className="h-6" />
              </div>
            </div>

            <PaymentTabs
              userRole={customerInfo?.role || 'user'}
              allowedRoles={['user','admin']}
              showPaypal={false}
              onTabChange={handlePaymentTabChange}
            />
            {paymentTab === 'new-card' && (
              <>
                <div className="flex flex-col md:grid md:grid-cols-2 gap-3 mb-5">
                  <div className="flex flex-col">
                    <label className={labelClassName}>
                      First name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="John"
                      value={cardData.firstName || ''}
                      onChange={handleCardholderFirstNameChange}
                      onBlur={handleCardholderFirstNameChange}
                      className={clsx(
                        inputClassName,
                        validationErrors.firstName ? 'border-red-300' : 'border-gray-300',
                        'focus:outline-none focus:border-blue-500'
                      )}
                    />
                    {validationErrors.firstName && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.firstName}</p>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <label className={labelClassName}>
                      Last name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Doe"
                      value={cardData.lastName || ''}
                      onChange={handleCardholderLastNameChange}
                      onBlur={handleCardholderLastNameChange}
                      className={clsx(
                        inputClassName,
                        validationErrors.lastName ? 'border-red-300' : 'border-gray-300',
                        'focus:outline-none focus:border-blue-500'
                      )}
                    />
                    {validationErrors.lastName && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.lastName}</p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col md:grid md:grid-cols-3 gap-3 mb-5">
                  <div className="flex flex-col">
                    <label className={labelClassName}>
                      Card number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="xxxx xxxx xxxx xxxx"
                      value={cardData.number || ''}
                      onChange={handleCardNumberChange}
                      onBlur={handleCardNumberChange}
                      className={clsx(
                        inputClassName,
                        validationErrors.number ? 'border-red-300' : 'border-gray-300',
                        'focus:outline-none focus:border-blue-500'
                      )}
                    />
                    {validationErrors.number && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.number}</p>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <label className={labelClassName}>
                      Expiration date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="mm/yy"
                      value={cardData.expiry || ''}
                      onChange={handleExpiryChange}
                      onBlur={handleExpiryChange}
                      maxLength={5}
                      className={clsx(
                        inputClassName,
                        validationErrors.expiry ? 'border-red-300' : 'border-gray-300',
                        'focus:outline-none focus:border-blue-500'
                      )}
                    />
                    {validationErrors.expiry && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.expiry}</p>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <label className={labelClassName}>
                      CVV <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="xxx"
                      value={cardData.cvv || ''}
                      onChange={handleCvvChange}
                      onBlur={handleCvvChange}
                      maxLength={4}
                      className={clsx(
                        inputClassName,
                        validationErrors.cvv ? 'border-red-300' : 'border-gray-300',
                        'focus:outline-none focus:border-blue-500'
                      )}
                    />
                    {validationErrors.cvv && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.cvv}</p>
                    )}
                  </div>
                </div>
              </>
            )}
            
            {paymentTab === 'previous-cards' && (
              <PreviousCardSelector
                isVisible={true}
                loading={loadingPreviousCards}
                loaded={loadedPreviousCards}
                cards={previousCardOptions}
                selectedCardId={cardData.previousCard?.id}
                onSelectCard={handleSelectPreviousCard}
              />
            )}

            {(paymentTab === 'new-card' || paymentTab === 'previous-cards') && (
              <BillingAddressSelector
                isVisible={true}
                billingCountry={cardData.billingCountry}
                billingZipCode={cardData.billingZipCode}
                onCountryChange={handleBillingCountryChange}
                onZipCodeChange={handleBillingZipCodeChange}
                validationErrors={validationErrors}
                showFlags={true}
              />
            )}

            {paymentTab === 'club-billing' && (
              <ClubBillingSelector
                isVisible={true}
                loading={loadingClubs}
                loaded={loadedClubs}
                clubs={clubBillingOptions}
                selectedClubId={cardData.clubBilling?.club_id}
                onSelectClub={handleSelectClubBilling}
              />
            )}
            
            {/* Save Payment Method Button and Status */}
            <div className="mt-6">
              <p className="text-center text-xs text-gray-500 mt-2">
                Your card information is encrypted for security
              </p>
            </div>
          </div>
        )}
      </div>

      {creatingOrder && (
        <LoadingState
          className="border-none" message="Creating your order..."
        />
      )}

      {(orderError || cartError) && (
          <div className={clsx(
            'mt-4 p-3 rounded-lg border',
            {
              'bg-red-50 border-red-200 text-red-600': !isDark,
              'bg-red-900/20 border-red-600 text-red-400': isDark
            }
        )}>
          {orderError || cartError}
        </div>
      )}
      {(orderProgress && (
        <div className={clsx(
          'mt-4 p-3 text-center rounded-lg border',
          {
            'bg-green-50 border-green-200 text-green-600': !isDark,
            'bg-green-900/20 border-green-600 text-green-400': isDark
          }
        )}>
          {orderProgress}
        </div>
      ))}

      {/* Footer with navigation buttons */}
      <div className="hidden md:block mt-8">
        <ShippingFlowFooter
          onBack={() => onClickBack?.()}
          showBack={true}
          showPrimaryButton={true}
          showSecondaryButton={false}
          onPrimaryAction={() => onClickContinue?.()}
          primaryButtonText="Book Now"
          primaryButtonContainerClass="hidden md:block"
        />
      </div>
    </>
  );
};

PaymentForm.propTypes = {
  /** Function to create payment method and tokenize card */
  createPaymentMethod: PropTypes.func.isRequired,
  onCardDataChange: PropTypes.func.isRequired,
  onApplyPromoCode: PropTypes.func.isRequired,
  onRemovePromoCode: PropTypes.func.isRequired,
  applyingPromoCode: PropTypes.bool,
  appliedPromoCode: PropTypes.bool,
  promoCodeError: PropTypes.string,
  creatingOrder: PropTypes.bool.isRequired,
  cartError: PropTypes.string,
  orderError: PropTypes.string,
  orderProgress: PropTypes.string,
  onClickContinue: PropTypes.func,
  onClickBack: PropTypes.func
};

export default PaymentForm;